import React from 'react';
import {
  TrendingUp,
  TrendingDown,
  Info,
  Download,
  Share2,
  Calendar,
  ChevronDown,
  Instagram,
  Youtube,
  Video,
  ArrowUpRight,
  InfoIcon,
  Sparkles
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { motion } from 'motion/react';

// --- Mock Data ---

const campaignAssets = [
  {
    id: 'winner',
    label: 'Asset 1',
    isWinner: true,
    creator: 'Julia Martinez',
    avatar: 'https://images.unsplash.com/photo-1712168567852-ea607c2d3177?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=100',
    caption: 'Get ready for the Summer Glow campaign with my favorite skincare routine! ✨ #SummerGlow #Skincare',
    videoUrl: 'https://player.cloudinary.com/embed/?cloud_name=dmudrz5qs&public_id=blob_00583cdb_rmxqyb',
    impactScore: '9.2/10',
    engagement: '5.2k',
    liftData: {
      relevance: 89,
      association: 82,
      purchaseIntent: 52
    },
    aiAnalysis: "The high relevance score (89%) indicates that Julia's audience strongly identifies with the 'Summer Glow' value proposition. The 22% lift over control suggests the creative successfully bridged the gap between awareness and personal utility. Recommendation: Scale similar aesthetic-focused content for top-of-funnel conversion."
  },
  {
    id: 'asset2',
    label: 'Asset 2',
    isWinner: false,
    creator: 'Tom Harrison',
    avatar: 'https://images.unsplash.com/photo-1714331251780-db56109a9887?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=100',
    caption: 'Testing the new glow serum in real daylight conditions. The texture is amazing! ☀️',
    videoUrl: 'https://player.cloudinary.com/embed/?cloud_name=dmudrz5qs&public_id=WhatsApp_Video_2025-10-01_at_13.21_b6c50f0f_g3pxfc',
    impactScore: '8.4/10',
    engagement: '4.8k',
    liftData: {
      relevance: 84,
      association: 78,
      purchaseIntent: 48
    },
    aiAnalysis: "Tom's technical approach to the serum texture drove significant trust, reflected in the 19% association lift. While purchase intent is lower than Asset 1, the quality of engagement suggests a more educated lead. Recommendation: Retarget this audience with Asset 1 to close the purchase gap."
  },
  {
    id: 'asset3',
    label: 'Asset 3',
    isWinner: false,
    creator: 'Fiona Chen',
    avatar: 'https://images.unsplash.com/photo-1669502299593-5dbb23edfdb4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=100',
    caption: 'Quick GRWM using the full Summer Glow kit. Stay tuned for the results! ✨',
    videoUrl: 'https://player.cloudinary.com/embed/?cloud_name=dmudrz5qs&public_id=blob_78a5ef79_vxanlp',
    impactScore: '7.9/10',
    engagement: '3.9k',
    liftData: {
      relevance: 79,
      association: 72,
      purchaseIntent: 42
    },
    aiAnalysis: "The 'Get Ready With Me' format continues to be a reliability anchor. A 15% lift in purchase intent from a smaller reach indicates high conversion efficiency. Recommendation: Deploy this asset in paid social 'Shop Now' units."
  },
  {
    id: 'asset4',
    label: 'Asset 4',
    isWinner: false,
    creator: 'Marcus Wright',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=100',
    caption: 'Behind the scenes of our latest shoot. This energy is unmatched! 🎬',
    videoUrl: 'https://player.cloudinary.com/embed/?cloud_name=dmudrz5qs&public_id=blob_ed0d3198_fxm7ac',
    impactScore: '8.1/10',
    engagement: '4.2k',
    liftData: {
      relevance: 81,
      association: 75,
      purchaseIntent: 46
    },
    aiAnalysis: "Behind-the-scenes content humanizes the brand, contributing to a solid 15% relevance lift. This asset performs best as a brand-building tool rather than a direct driver of intent. Recommendation: Use for low-frequency retargeting to maintain brand affinity."
  }
];

const allCampaignAggregated = {
  id: 'all',
  label: 'All Campaign',
  creator: 'Aggregated View',
  caption: 'Performance across all 24 active campaign assets',
  videoUrl: 'https://player.cloudinary.com/embed/?cloud_name=dmudrz5qs&public_id=blob_00583cdb_rmxqyb',
  impactScore: '8.2/10',
  engagement: '124.5k',
  liftData: {
    relevance: 82,
    association: 75,
    purchaseIntent: 45
  },
  aiAnalysis: "The campaign as a whole is outperforming FMCG benchmarks by an average of 6.2% across all lift categories. The synergy between high-energy BTS content and polished tutorials has created a multi-touchpoint effect. Recommendation: Maintain current creator mix but shift 15% more budget towards the 'Winner' aesthetic for the final flight."
};

const liftBaselines = {
  control: {
    relevance: 67,
    association: 63,
    purchaseIntent: 37
  },
  benchmark: {
    relevance: 74,
    association: 68,
    purchaseIntent: 41
  }
};

const performanceMetrics = [
  { label: 'CPM', value: '$12.40', trend: '+2.4%', isPositive: true, chartData: [10, 12, 11, 13, 12.4] },
  { label: 'CPC', value: '$0.85', trend: '-5.1%', isPositive: true, chartData: [1.1, 1.0, 0.9, 0.88, 0.85] },
  { label: 'CTR', value: '1.24%', trend: '+0.12%', isPositive: true, chartData: [1.1, 1.15, 1.12, 1.2, 1.24] },
  { label: 'Engagement Rate', value: '4.82%', trend: '+0.5%', isPositive: true, chartData: [4.2, 4.5, 4.3, 4.7, 4.82] },
  { label: 'Impressions', value: '2.48M', trend: '+12%', isPositive: true, chartData: [1.8, 2.0, 2.2, 2.3, 2.48] },
];

const _liftGroups = [
  {
    title: 'Relevance Lift',
    metrics: [
      { label: 'Current asset', value: 89, lift: '+22%', type: 'test' },
      { label: 'Control', value: 67, lift: '', type: 'control' },
      { label: 'All Campaign assets', value: 82, lift: '+15%', type: 'test' },
      { label: 'Benchmark (FCMG)', value: 74, lift: '+7%', type: 'test' },
    ]
  },
  {
    title: 'Association Lift',
    metrics: [
      { label: 'Current asset', value: 82, lift: '+19%', type: 'test' },
      { label: 'Control', value: 63, lift: '', type: 'control' },
      { label: 'All Campaign assets', value: 75, lift: '+12%', type: 'test' },
      { label: 'Benchmark (FCMG)', value: 68, lift: '+5%', type: 'test' },
    ]
  },
  {
    title: 'Purchase Intent',
    metrics: [
      { label: 'Current asset', value: 52, lift: '+15%', type: 'test' },
      { label: 'Control', value: 37, lift: '', type: 'control' },
      { label: 'All Campaign assets', value: 45, lift: '+8%', type: 'test' },
      { label: 'Benchmark (FCMG)', value: 41, lift: '+4%', type: 'test' },
    ]
  }
];

const influencerData = [
  {
    name: 'Julia Martinez',
    handle: '@julia_style',
    avatar: 'https://images.unsplash.com/photo-1712168567852-ea607c2d3177?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=100',
    platform: 'Instagram',
    reach: '450.2k',
    engagement: '5.24%',
    liftContribution: '+3.2%',
  },
  {
    name: 'Tom Harrison',
    handle: '@tech_tom',
    avatar: 'https://images.unsplash.com/photo-1714331251780-db56109a9887?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=100',
    platform: 'TikTok',
    reach: '820.5k',
    engagement: '6.82%',
    liftContribution: '+4.5%',
  },
  {
    name: 'Fiona Chen',
    handle: '@fit_fiona',
    avatar: 'https://images.unsplash.com/photo-1669502299593-5dbb23edfdb4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=100',
    platform: 'YouTube',
    reach: '210.8k',
    engagement: '4.15%',
    liftContribution: '+2.1%',
  },
];

// --- Components ---

const MetricMicroChart = ({ data, color }: { data: number[], color: string }) => {
  const chartData = data.map((val, i) => ({ val, i }));
  return (
    <div className="h-8 w-16">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData}>
          <Line
            type="monotone"
            dataKey="val"
            stroke={color}
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={true}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const PerformanceCard = ({ metric }: { metric: typeof performanceMetrics[0] }) => {
  return (
    <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow duration-200">
      <CardContent className="p-5">
        <div className="flex justify-between items-start mb-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{metric.label}</p>
          <MetricMicroChart data={metric.chartData} color="#101828" />
        </div>
        <div className="flex items-baseline gap-2">
          <h3 className="text-2xl font-bold text-[#101828]">{metric.value}</h3>
          <div className={`flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${metric.isPositive ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            {metric.isPositive ? <TrendingUp className="w-2.5 h-2.5 mr-0.5" /> : <TrendingDown className="w-2.5 h-2.5 mr-0.5" />}
            {metric.trend}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ComparisonBar = ({ metric, index }: { metric: typeof _liftGroups[0]['metrics'][0], index: number }) => {
  const isControl = metric.type === 'control';
  const isBenchmark = metric.label.includes('Benchmark');

  const barColor = isControl
    ? 'bg-gray-100'
    : isBenchmark
      ? 'bg-[#ff0080]/30'
      : 'bg-[#ff0080]';

  return (
    <div className="group/bar mb-4 last:mb-0">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <span className={`text-[12px] font-bold ${isControl ? 'text-gray-300' : isBenchmark ? 'text-[#475467]/70' : 'text-[#475467]'}`}>
            {metric.label}
          </span>
          {isBenchmark && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <InfoIcon className="w-3 h-3 text-gray-300 cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-[10px]">Category average across similar FMCG campaigns</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        {metric.lift && (
          <span className={`text-[12px] font-bold ${isBenchmark ? 'text-[#ff0080]/60' : 'text-[#ff0080]'}`}>
            {metric.lift}
          </span>
        )}
      </div>
      <div className={`relative h-[24px] bg-gray-50/50 rounded-full overflow-hidden border border-gray-100/50 ${isBenchmark ? 'border-dashed' : ''}`}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${metric.value}%` }}
          transition={{ duration: 1, delay: index * 0.05 }}
          className={`h-full rounded-full ${barColor} shadow-sm transition-all duration-1000 ease-out`}
        />
        <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
          <span className={`text-[11px] font-bold ${isControl ? 'text-gray-400' : isBenchmark ? 'text-gray-400' : 'text-[#475467]'}`}>
            {metric.value}%
          </span>
        </div>
      </div>
    </div>
  );
};

export const BrandLiftPage: React.FC = () => {
  const [selectedAssetId, setSelectedAssetId] = React.useState<string>('winner');

  const selectedAsset = selectedAssetId === 'all'
    ? allCampaignAggregated
    : campaignAssets.find(a => a.id === selectedAssetId) || campaignAssets[0];

  const getLiftGroups = () => [
    {
      title: 'Brand Lift',
      metrics: [
        { label: 'Current asset', value: selectedAsset.liftData.relevance, lift: `+${selectedAsset.liftData.relevance - liftBaselines.control.relevance}%`, type: 'test' },
        { label: 'Control', value: liftBaselines.control.relevance, lift: '', type: 'control' },
        { label: 'All Campaign assets', value: allCampaignAggregated.liftData.relevance, lift: `+${allCampaignAggregated.liftData.relevance - liftBaselines.control.relevance}%`, type: 'test' },
        { label: 'Benchmark (FCMG)', value: liftBaselines.benchmark.relevance, lift: `+${liftBaselines.benchmark.relevance - liftBaselines.control.relevance}%`, type: 'test' },
      ]
    },
    {
      title: 'Association Lift',
      metrics: [
        { label: 'Current asset', value: selectedAsset.liftData.association, lift: `+${selectedAsset.liftData.association - liftBaselines.control.association}%`, type: 'test' },
        { label: 'Control', value: liftBaselines.control.association, lift: '', type: 'control' },
        { label: 'All Campaign assets', value: allCampaignAggregated.liftData.association, lift: `+${allCampaignAggregated.liftData.association - liftBaselines.control.association}%`, type: 'test' },
        { label: 'Benchmark (FCMG)', value: liftBaselines.benchmark.association, lift: `+${liftBaselines.benchmark.association - liftBaselines.control.association}%`, type: 'test' },
      ]
    },
    {
      title: 'Purchase Intent',
      metrics: [
        { label: 'Current asset', value: selectedAsset.liftData.purchaseIntent, lift: `+${selectedAsset.liftData.purchaseIntent - liftBaselines.control.purchaseIntent}%`, type: 'test' },
        { label: 'Control', value: liftBaselines.control.purchaseIntent, lift: '', type: 'control' },
        { label: 'All Campaign assets', value: allCampaignAggregated.liftData.purchaseIntent, lift: `+${allCampaignAggregated.liftData.purchaseIntent - liftBaselines.control.purchaseIntent}%`, type: 'test' },
        { label: 'Benchmark (FCMG)', value: liftBaselines.benchmark.purchaseIntent, lift: `+${liftBaselines.benchmark.purchaseIntent - liftBaselines.control.purchaseIntent}%`, type: 'test' },
      ]
    }
  ];

  const liftGroups = getLiftGroups();

  return (
    <div className="min-h-screen bg-[#f8f9fa] p-4 lg:p-6 space-y-6 font-inter">
      {/* Top Navigation / Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-white rounded-lg shadow-sm border border-gray-100 flex items-center justify-center overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1599305445671-ac291c95aba9?w=100&h=100&fit=crop"
              alt="Brand Logo"
              className="w-5 h-5 object-contain"
            />
          </div>
          <div>
            <h1 className="text-lg font-bold text-[#101828]">Summer Glow 2024</h1>
            <p className="text-[10px] text-gray-500">Influencer Performance Report</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" className="bg-white border-gray-200 text-gray-700 h-8 px-3 gap-1.5 text-[10px]">
            <Calendar className="w-3.5 h-3.5 text-gray-400" />
            Jun 1, 2024 - Jul 15, 2024
            <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
          </Button>
          <Button variant="outline" className="bg-white border-gray-200 text-gray-700 h-8 px-2.5 gap-1.5 text-[10px]">
            <Download className="w-3.5 h-3.5 text-gray-400" />
            Export
          </Button>
          <Button className="bg-[#2e024c] hover:bg-[#3a0d5e] text-white h-8 px-3 gap-1.5 text-[10px]">
            <Share2 className="w-3.5 h-3.5" />
            Share Report
          </Button>
        </div>
      </div>

      {/* 1. Performance Metrics Section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {performanceMetrics.map((metric, i) => (
          <PerformanceCard key={i} metric={metric} />
        ))}
      </div>

      {/* 2. Brand Results Section - New Updated Style */}
      <Card className="bg-white border-0 shadow-sm overflow-hidden mt-[0px] mr-[0px] mb-[14px] ml-[0px]">
        <CardHeader className="px-6 py-3 border-b border-gray-50 flex flex-col md:flex-row md:items-start justify-between space-y-4 md:space-y-0">
          <div className="space-y-0.5">
            <div className="flex items-center gap-3">
              <CardTitle className="text-base font-bold text-[#101828]">Brand Impact Results</CardTitle>
              <Badge variant="outline" className="text-[9px] font-bold text-green-600 border-green-200 bg-green-50 px-1.5 py-0 h-4">
                High Confidence
              </Badge>
            </div>
            <p className="text-[11px] font-medium text-[#101828]">
              Overall Brand Impact: <span className="text-[#ff0080]">Strong</span> — above-benchmark lift across metrics.
            </p>
          </div>
          <div className="flex items-center gap-5">
            <div className="hidden lg:flex items-center gap-5 text-[9px] font-bold text-gray-400 uppercase tracking-widest">
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-gray-200" />
                Control
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-[#ff0080]" />
                Lift
              </div>
            </div>
            <div className="flex items-center gap-1 p-0.5 bg-gray-100/50 rounded-lg border border-gray-200/50">
              <button className="h-5 text-[8px] font-bold px-1.5 bg-white shadow-sm rounded-md text-[#101828]">Lift Raw</button>
              <button className="h-5 text-[8px] font-bold px-1.5 text-gray-400 hover:text-gray-600">Lift Chart</button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-6 py-4">
          <div className="flex items-center gap-1 mb-5 p-0.5 bg-gray-50 rounded-lg border border-gray-100 w-fit">
            {campaignAssets.map((asset) => (
              <button
                key={asset.id}
                onClick={() => setSelectedAssetId(asset.id)}
                className={`h-7 px-3 text-[10px] font-bold rounded-md transition-all ${
                  selectedAssetId === asset.id
                    ? 'bg-white shadow-sm text-[#101828] border border-gray-100'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                {asset.label}
              </button>
            ))}
            <div className="w-px h-3 bg-gray-200 mx-0.5" />
            <button
              onClick={() => setSelectedAssetId('all')}
              className={`h-7 px-3 text-[10px] font-bold rounded-md transition-all ${
                selectedAssetId === 'all'
                  ? 'bg-[#be43ff] shadow-sm text-white'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              All Assets
            </button>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-4 gap-8 lg:gap-12">
            {/* Featured Content Column (Left side - 1/4 on XL) */}
            <div className="xl:col-span-1 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-[10px] font-bold text-[#101828] uppercase tracking-widest leading-tight">
                  {selectedAssetId === 'all' ? 'Campaign-Wide Impact' : 'Top Performing Creative'}<br/>
                  <span className="text-gray-400 text-[9px]">({selectedAssetId === 'all' ? 'Across 24 assets' : 'Driving Highest Lift'})</span>
                </h4>
                {selectedAssetId === 'winner' && <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 border-0 text-[9px] font-bold uppercase">Winner</Badge>}
              </div>

              <div className="relative aspect-[9/16] bg-black rounded-2xl overflow-hidden shadow-2xl group cursor-pointer border border-gray-100">
                <iframe
                  key={selectedAsset.videoUrl}
                  src={`${selectedAsset.videoUrl}&autoplay=true&loop=true&muted=true`}
                  className="w-full h-full border-0"
                  allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
                  title="Featured Campaign Video"
                />

                {/* Overlay Info */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Avatar className="w-6 h-6 border border-white/20">
                      <AvatarImage src={(selectedAsset as { avatar?: string }).avatar} />
                    </Avatar>
                    <span className="text-xs font-semibold text-white">{selectedAsset.creator}</span>
                  </div>
                  <p className="text-[10px] text-white/80 line-clamp-2">{selectedAsset.caption}</p>
                </div>

                {/* Status Indicator */}
                <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 bg-black/40 backdrop-blur-md rounded-full border border-white/10">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-[9px] font-bold text-white uppercase tracking-wider">LIVE DATA</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="p-2.5 bg-gray-50 rounded-xl border border-gray-100">
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1">Impact Score</p>
                  <p className="text-sm font-bold text-[#101828]">{selectedAsset.impactScore}</p>
                </div>
                <div className="p-2.5 bg-gray-50 rounded-xl border border-gray-100">
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1">Engagement</p>
                  <p className="text-sm font-bold text-[#101828]">{selectedAsset.engagement}</p>
                </div>
              </div>
            </div>

            {/* Charts Column (Right side - 3/4 on XL) */}
            <div className="xl:col-span-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
                {liftGroups.map((group, groupIdx) => (
                  <div key={groupIdx} className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-[11px] font-bold text-[#101828] uppercase tracking-widest">{group.title}</h4>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <InfoIcon className="w-3.5 h-3.5 text-gray-300 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">Measured via exposed vs control audience</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="space-y-3">
                      {group.metrics.map((metric, i) => (
                        <ComparisonBar key={i} metric={metric} index={i} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-6 bg-gradient-to-br from-purple-50/50 via-white to-pink-50/30 rounded-2xl border border-purple-100/50 shadow-sm relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:scale-110 transition-transform duration-500">
                  <Sparkles className="w-12 h-12 text-[#be43ff]" />
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-lg bg-[#be43ff] flex items-center justify-center">
                    <Sparkles className="w-3.5 h-3.5 text-white" />
                  </div>
                  <h5 className="text-[11px] font-bold text-[#101828] uppercase tracking-widest">Expert AI Analysis</h5>
                </div>
                <p className="text-[13px] font-medium text-[#475467] leading-relaxed relative z-10">
                  {selectedAsset.aiAnalysis}
                </p>
                <div className="mt-4 flex items-center gap-2">
                  <Badge variant="outline" className="bg-white/80 border-purple-100 text-[#be43ff] text-[9px] font-bold px-2 py-0">Strategic Insight</Badge>
                  <Badge variant="outline" className="bg-white/80 border-pink-100 text-[#ff0080] text-[9px] font-bold px-2 py-0">Actionable</Badge>
                </div>
              </div>

              <div className="mt-6 p-5 bg-gradient-to-br from-gray-50/50 to-white rounded-xl border border-gray-100 flex items-start gap-4">
                <div className="p-2 bg-white rounded-lg shadow-sm border border-gray-50">
                  <Info className="w-4 h-4 text-[#ff0080]" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-bold text-[#101828]">Methodology</p>
                  <p className="text-[11px] text-gray-500 leading-relaxed mt-1">
                    We replicate real-world social environments using global panelists to capture real opinions, providing fast and reliable data to understand your campaign's true influence.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 3. Influencer Performance Breakdown */}
      <Card className="bg-white border-0 shadow-sm overflow-hidden">
        <CardHeader className="p-6 border-b border-gray-50">
          <CardTitle className="text-lg font-bold text-[#101828]">Influencer Performance Breakdown</CardTitle>
          <p className="text-xs text-gray-500 mt-1">Contribution analysis by individual creator</p>
        </CardHeader>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-gray-50/50">
              <TableRow className="border-b border-gray-100 hover:bg-transparent">
                <TableHead className="text-[11px] font-bold text-gray-500 uppercase tracking-wider pl-6">Creator</TableHead>
                <TableHead className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Platform</TableHead>
                <TableHead className="text-[11px] font-bold text-gray-500 uppercase tracking-wider text-right">Reach</TableHead>
                <TableHead className="text-[11px] font-bold text-gray-500 uppercase tracking-wider text-right">Engagement</TableHead>
                <TableHead className="text-[11px] font-bold text-gray-500 uppercase tracking-wider text-right pr-6">Brand Lift Contribution</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {influencerData.map((influencer, i) => (
                <TableRow key={i} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                  <TableCell className="py-4 pl-6">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-9 h-9 border border-gray-100 shadow-sm">
                        <AvatarImage src={influencer.avatar} />
                        <AvatarFallback>{influencer.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-semibold text-[#101828]">{influencer.name}</p>
                        <p className="text-xs text-gray-500">{influencer.handle}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {influencer.platform === 'Instagram' && <Instagram className="w-3.5 h-3.5 text-pink-500" />}
                      {influencer.platform === 'TikTok' && <Video className="w-3.5 h-3.5 text-black" />}
                      {influencer.platform === 'YouTube' && <Youtube className="w-3.5 h-3.5 text-red-600" />}
                      <span className="text-xs font-medium text-gray-700">{influencer.platform}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="text-sm font-semibold text-[#101828]">{influencer.reach}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="text-sm font-semibold text-[#101828]">{influencer.engagement}</span>
                  </TableCell>
                  <TableCell className="text-right pr-6">
                    <div className="flex items-center justify-end gap-1.5">
                      <span className="text-sm font-bold text-[#ff0080]">{influencer.liftContribution}</span>
                      <ArrowUpRight className="w-3 h-3 text-[#ff47a3]" />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="p-4 border-t border-gray-50 bg-gray-50/20 flex justify-center">
          <Button variant="ghost" className="text-xs font-bold text-gray-500 hover:text-[#ff0080] transition-colors">
            View All Creators (24)
          </Button>
        </div>
      </Card>
    </div>
  );
};
